/**
 * 验收标准确认书 · labor-portal section on PortalEngagement. Appears once 估价 releases the PM's standards:
 * review criteria → ask the PM questions → 确认清楚 → review the AI-composed annexure → 确认 or
 * 要求修改 → (finalized) download the Word copy.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  portalStandardsWorkflow, portalWorkflowActions, annexureDownloadUrl, portalWorkflowDocUrl, WF_STATUS,
  apiErrText as errText, type WorkflowStatus,
} from '../lib/api';
import MdView from './MdView';
import { toast } from './ui/Toast';

export default function PortalStandardsWorkflowSection({ eid }: { eid: string }) {
  const qc = useQueryClient();
  const { data, isError, error, refetch } = useQuery({ queryKey: ['portal-standards-workflow', eid], queryFn: () => portalStandardsWorkflow(eid), refetchInterval: 30_000 });
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [revising, setRevising] = useState(false);
  const [comment, setComment] = useState('');

  // A 401 / 500 / network blip used to blank the WHOLE section — including the 「确认清楚」 button that
  // is the only way out of `released` (and the 30s poll could blink it away mid-session). Say so instead.
  if (isError) {
    return (
      <div style={{ marginTop: 18 }}>
        <h3>验收标准确认（签约前）/ Acceptance standards confirmation</h3>
        <div className="card small" style={{ borderColor: 'var(--bad)', color: 'var(--bad)' }}>
          加载失败：{errText(error)}
          <button style={{ marginLeft: 8 }} onClick={() => void refetch()}>重试 / retry</button>
        </div>
      </div>
    );
  }
  if (!data?.status) return null;
  const status = data.status as WorkflowStatus;
  const annexure = data.annexure ?? null;

  async function act(fn: () => Promise<unknown>, okMsg?: string) {
    setBusy(true);
    try { await fn(); if (okMsg) toast(okMsg, { kind: 'success' }); }
    catch (e) { toast(errText(e), { kind: 'error' }); }
    finally { setBusy(false); await qc.invalidateQueries({ queryKey: ['portal-standards-workflow', eid] }); }
  }

  return (
    <div style={{ marginTop: 18 }}>
      <h3>验收标准确认（签约前）/ Acceptance standards confirmation</h3>
      <div className="card" style={{ marginBottom: 8 }}>
        <div className="row-wrap" style={{ alignItems: 'center', marginBottom: 6 }}>
          <span className={`pill small ${WF_STATUS[status]?.pill}`}>{WF_STATUS[status]?.label}</span>
          {status === 'released' && <span className="muted small">请逐条查看，有疑问向项目经理提问；全部清楚后点「确认清楚」。/ Review each item; ask the PM anything unclear, then mark clear.</span>}
        </div>

        {data.criteria.map((c) => (
          <div key={c.id} style={{ padding: '4px 0', borderBottom: '1px solid var(--rule, #efe9dc)' }}>
            <div className="small">
              {c.trade && <span className="pill pill-muted small" style={{ marginRight: 6 }}>{c.trade}</span>}
              <strong>{c.label_zh || c.label_en || '—'}</strong>
              {c.is_critical && <span className="pill small pill-bad" style={{ marginLeft: 6 }}>关键 / critical</span>}
            </div>
            <MdView md={c.pass_rule_md} className="muted small" />
          </div>
        ))}

        {/* 标准文件 — the project's own standards documents, released with the criteria */}
        {(data.docs?.length ?? 0) > 0 && (
          <div style={{ marginTop: 10 }}>
            <strong className="small">标准文件 / Standards documents（{data.docs!.length}）</strong>
            <ul className="small" style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {data.docs!.map((d) => (
                <li key={d.id}>
                  <a href={portalWorkflowDocUrl(eid, d.id)}>{d.file_name}</a>
                  {d.category && <span className="pill pill-muted small" style={{ marginLeft: 6 }}>{d.category}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Q&A thread with the PM */}
        {(data.messages.length > 0 || status === 'released') && (
          <div style={{ marginTop: 10 }}>
            <strong className="small">向项目经理提问 / Questions to the PM（{data.messages.length}）</strong>
            {data.messages.map((m) => (
              <div key={m.id} className="small" style={{ padding: '3px 0' }}>
                <span className={`pill small ${m.direction === 'inbound' ? 'pill-stage' : 'pill-good'}`}>{m.direction === 'inbound' ? '我方' : '项目经理'}</span>{' '}
                <span className="muted">{m.created_at.slice(5, 16).replace('T', ' ')}</span>
                <div style={{ whiteSpace: 'pre-wrap' }}>{m.body_md}</div>
              </div>
            ))}
            {status === 'released' && (
              <div className="row-wrap" style={{ gap: 6, marginTop: 6 }}>
                <input className="grow" style={{ minWidth: 160 }} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="对验收标准的疑问… / any question about the standards" />
                <button disabled={busy || !question.trim()} onClick={() => act(async () => { await portalWorkflowActions.ask(eid, question); setQuestion(''); }, '已发送 / sent')}>提问 / Ask</button>
              </div>
            )}
          </div>
        )}

        {status === 'released' && (
          <div style={{ marginTop: 10 }}>
            <button className="btn-primary" disabled={busy} onClick={() => {
              if (confirm('确认您已完全理解全部验收标准？确认后将安排双方确认会议。\n/ Confirm you fully understand all standards? A confirmation meeting follows.')) {
                void act(() => portalWorkflowActions.clear(eid), '已确认清楚 / marked clear');
              }
            }}>✓ 确认清楚，无更多问题 / All clear</button>
          </div>
        )}
        {status === 'labor_clear' && <div className="small" style={{ color: 'var(--good)', marginTop: 6 }}>✓ 已确认清楚 — 等待确认会议与验收标准确认书。/ Clear — awaiting the meeting and the annexure.</div>}
      </div>

      {annexure && (status === 'annexure_review' || status === 'revision_requested' || status === 'finalized') && (
        <div className="card" style={{ marginBottom: 8 }}>
          <div className="row-wrap" style={{ alignItems: 'center', marginBottom: 6 }}>
            <strong>验收标准确认书（第 {annexure.version} 版）</strong>
            {/* plain link, not a <button> inside an <a>: the nested form is invalid HTML and a
                known "does nothing" in embedded WebViews */}
            {/* no `download` attr: the route already sends Content-Disposition attachment, and on a
    409/422 error the JSON must render (a downloaded .json would hide the fix-it copy) */}
            <a className="btn small" href={annexureDownloadUrl(annexure.id)}>⬇ 下载 Word</a>
          </div>
          <MdView md={annexure.content_md} className="small" />
          {status === 'annexure_review' && !annexure.labor_confirmed_at && (
            !revising ? (
              <div className="row-wrap" style={{ marginTop: 8 }}>
                <button className="btn-primary" disabled={busy} onClick={() => act(() => portalWorkflowActions.confirmAnnexure(eid), '已确认 / confirmed')}>✓ 确认验收标准 / Confirm</button>
                <button disabled={busy} onClick={() => setRevising(true)}>✎ 要求修改 / Request revision</button>
              </div>
            ) : (
              <div className="row-wrap" style={{ alignItems: 'flex-end', marginTop: 8 }}>
                <div className="field" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
                  <label>修改意见（必填）/ comment</label>
                  <input value={comment} onChange={(e) => setComment(e.target.value)} />
                </div>
                <button className="btn-primary" disabled={busy || !comment.trim()} onClick={() => { void act(() => portalWorkflowActions.requestRevision(eid, comment), '已提交修改意见 / revision requested'); setRevising(false); }}>提交 / Submit</button>
                <button disabled={busy} onClick={() => setRevising(false)}>×</button>
              </div>
            )
          )}
          {status === 'annexure_review' && annexure.labor_confirmed_at && !annexure.pm_confirmed_at && (
            <div className="muted small" style={{ marginTop: 6 }}>您已确认 — 等待项目经理确认。/ You confirmed — waiting for the PM.</div>
          )}
          {status === 'revision_requested' && <div className="muted small" style={{ marginTop: 6 }}>修改意见已提交 — 项目经理将出具新版本。/ Revision requested — a new version is coming.</div>}
          {status === 'finalized' && <div className="small" style={{ color: 'var(--good)', marginTop: 6 }}>✓ 双方已确认定稿 — 本文件将作为合同附件。/ Finalized — attached to the contract as an appendix.</div>}
        </div>
      )}
    </div>
  );
}
